import type { UploadFile } from "antd"
import { Flex, Skeleton } from "antd"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { type ConnectedProps, connect } from "react-redux"
import { useParams } from "react-router"

import { useRenameAttachment } from "../../../../../../hooks/use-rename-attachment.ts"
import type { RootState } from "../../../../../../store.ts"
import { validateFile } from "../../../../../../utils/file-validation.ts"
import { serializeData } from "../../../../../../utils/utils"
import { albumSetProp } from "../../../../../Album/redux/album.slice.ts"
import { addAlbumAttachments } from "../../../../../Album/redux/album.thunks.ts"
import { type AttachmentFilters } from "../../../../../Album/redux/album.types.ts"
import {
  getAttachments,
  getAttachmentsFilterOptions,
  getMoreAttachments,
  renameAttachment
} from "../../../../../Album/redux/attachments.thunks.ts"
import {
  type Attachment,
  AttachmentModels
} from "../../../../../Album/types/attachment.types.ts"
import { selectProjectWritePermissions } from "../../../../helpers/project-permissions.selectors.ts"
import {
  deleteProjectAttachment,
  uploadProjectAttachment
} from "../../../../redux/project-attachments/project-attachments.thunks.ts"
import { AlbumFilesSettings } from "../../../constants/album-files-settings.tsx"
import { FileDragOverlay } from "../../FileDragOverlay.tsx"
import { ProjectAllFiles } from "./ProjectAllFiles/ProjectAllFiles.tsx"
import { ProjectAllFilesFilters } from "./ProjectAllFiles/ProjectAllFilesFilters.tsx"

const mapAttachmentToUploadFile = (
  attachment: Attachment
): UploadFile<Attachment> => ({
  uid: attachment.id,
  name: attachment.original_filename,
  status: "done",
  url: attachment.file,
  size: attachment.file_size,
  type: attachment.mime_type,
  response: attachment
})

function ProjectViewAllFilesSection({
  userId,
  data,
  fetching,
  projectPermissions,
  getAttachments,
  getFilterOptions,
  getMoreAttachments,
  uploadAttachment,
  deleteAttachment,
  renameAttachment,
  filters,
  setProp,
  addAlbumAttachments
}: ConnectedProps<typeof connector>) {
  const projectId = useParams()?.id as string
  const files = data?.results || []
  const moduleDocs = projectPermissions?.docs

  const deleteAllowed = !!moduleDocs?.project_doc_delete
  const deleteForeignAllowed =
    deleteAllowed && !!moduleDocs?.project_doc_manage_others
  const addFilesAllowed = !!moduleDocs?.project_doc_create

  const editAllowed = !!moduleDocs?.project_doc_edit
  const editForeignAllowed =
    editAllowed && !!moduleDocs?.project_doc_manage_others

  const [uploadingFiles, setUploadingFiles] = useState<
    UploadFile<Attachment>[]
  >([])
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set())
  // const abortControllersRef = useRef<Map<string, AbortController>>(new Map())

  // Merge uploading files with fetched files
  const fileList = useMemo(() => {
    const fetchedFiles = files.map(mapAttachmentToUploadFile)
    return [...uploadingFiles, ...fetchedFiles]
  }, [files, uploadingFiles])

  const setFilters = useCallback(
    (data: Partial<AttachmentFilters>) => {
      setProp({
        key: "attachmentFilters",
        value: { ...filters, ...data }
      })
    },
    [setProp, filters]
  )

  const handleFilterChange = useCallback(
    async (data: Partial<AttachmentFilters>) => {
      setFilters({ ...data, page: 1 })
    },
    [setFilters]
  )

  const handleGetFilterOptions = useCallback(async () => {
    try {
      return await getFilterOptions()?.unwrap()
    } catch (e) {
      console.log("Error attachment filter options:", e)
      return null
    }
  }, [getFilterOptions])

  const handleGetAttachments = useCallback(() => {
    const serializeFilters = serializeData(filters || {})

    getAttachments({ project: projectId, ...serializeFilters })
  }, [projectId, filters, getAttachments])

  const handleLoadMoreData = useCallback(() => {
    if (!data?.next) return
    const urlObj = new URL(data?.next)
    const params = new URLSearchParams(urlObj.search)
    const paramsObj = Object.fromEntries(params)
    const serializeFilters = serializeData(paramsObj) as AttachmentFilters

    getMoreAttachments({ project: projectId, ...serializeFilters })
  }, [getMoreAttachments, projectId, data?.next])

  const handleUpload = useCallback(
    async (file: File) => {
      const uid = crypto.randomUUID()
      const abortController = new AbortController()

      // abortControllersRef.current.set(uid, abortController)

      setUploadingFiles((prev) => [
        {
          uid,
          name: file.name,
          status: "uploading",
          percent: 0
        },
        ...prev
      ])

      try {
        await uploadAttachment({
          data: {
            model_type: AttachmentModels.PROJECT,
            object_id: projectId,
            project_id: projectId,
            file
          },
          signal: abortController.signal
        }).unwrap()

        // abortControllersRef.current.delete(uid)

        // // Update uploading file with response data
        // setUploadingFiles((prev) =>
        //   prev.map((f) =>
        //     f.uid === uid
        //       ? {
        //           ...f,
        //           uid: res.id,
        //           status: "done",
        //           url: res.file,
        //           size: res.file_size,
        //           type: res.mime_type,
        //           response: res
        //         }
        //       : f
        //   )
        // )

        // Update uploading file with response data
        setUploadingFiles((prev) => prev.filter((f) => f.uid !== uid))

        handleGetAttachments()
      } catch (e) {
        // abortControllersRef.current.delete(uid)

        if (abortController.signal.aborted) {
          setUploadingFiles((prev) => prev.filter((f) => f.uid !== uid))
          return
        }

        setUploadingFiles((prev) =>
          prev.map((f) =>
            f.uid === uid
              ? {
                  ...f,
                  status: "error"
                }
              : f
          )
        )
      }
    },
    [uploadAttachment, handleGetAttachments, projectId]
  )

  const handleAddAttachments = async (
    albumId: string,
    attachmentId: string
  ) => {
    try {
      if (!albumId || !attachmentId) return

      await addAlbumAttachments({
        id: albumId,
        data: { attachment_ids: [attachmentId] }
      })
    } catch (e) {
      console.log("Error adding attachments:", e)
    }
  }

  // const handleCancelUpload = useCallback((uid: string) => {
  //   const abortController = abortControllersRef.current.get(uid)
  //   if (abortController) {
  //     abortController.abort()
  //     abortControllersRef.current.delete(uid)
  //   }
  // }, [])

  const handleBeforeUpload = useCallback(
    (file: File) => {
      const result = validateFile(file, {
        maxSize: AlbumFilesSettings.MAX_SIZE_MB,
        allowedExtensions: AlbumFilesSettings.ALLOWED_FILE_EXTENSIONS,
        defaultResult: true
      })

      if (result === true) {
        void handleUpload(file)
      }

      return false
    },
    [handleUpload]
  )

  const handleDelete = useCallback(
    async (attachmentId: string) => {
      setDeletingIds((prev) => new Set(prev).add(attachmentId))

      try {
        await deleteAttachment({
          attachment_id: attachmentId,
          object_id: projectId,
          model_type: AttachmentModels.PROJECT
        }).unwrap()

        // Remove from uploadingFiles if it was uploaded in this session
        setUploadingFiles((prev) => prev.filter((f) => f.uid !== attachmentId))

        // Refetch to update the list
        handleGetAttachments()
      } catch (e) {
        console.error("Failed to delete attachment:", e)
      } finally {
        setDeletingIds((prev) => {
          const next = new Set(prev)
          next.delete(attachmentId)
          return next
        })
      }
    },
    [deleteAttachment, projectId, handleGetAttachments]
  )

  const handleRename = useRenameAttachment(renameAttachment)

  useEffect(() => {
    void handleGetAttachments()
  }, [filters, handleGetAttachments])

  return (
    <Flex
      vertical
      gap="small"
      flex={1}
      className="overflow-hidden relative w-full"
    >
      <ProjectAllFilesFilters
        filters={filters}
        showRelatedFilter={
          !!projectPermissions?.docs?.project_doc_show_related_filter
        }
        projectId={projectId}
        onFilterChange={handleFilterChange}
        getFilterOptions={handleGetFilterOptions}
      />

      <FileDragOverlay
        message="Drag and drop files here"
        acceptedExtensions={AlbumFilesSettings.ALLOWED_FILE_EXTENSIONS}
        maxFileSize={AlbumFilesSettings.MAX_SIZE_MB}
        onFiles={(files) => files.forEach(handleBeforeUpload)}
      />

      {fetching && fileList.length === 0 ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-4 mt-4">
          {Array.from({ length: 21 }).map((_, i) => (
            <Skeleton.Node
              key={i}
              active
              style={{ width: "100%", height: "363px" }}
              className="w-full"
            />
          ))}
        </div>
      ) : (
        <ProjectAllFiles
          fileList={fileList}
          uploadAllowed={!!moduleDocs?.project_doc_create}
          currentUserId={userId}
          deleteForeignAllowed={deleteForeignAllowed}
          deleteAllowed={deleteAllowed}
          editAllowed={editAllowed}
          editForeignAllowed={editForeignAllowed}
          hasMore={!!data?.next}
          maxFileSize={AlbumFilesSettings.MAX_SIZE_MB}
          allowedExtensions={AlbumFilesSettings.ALLOWED_FILE_EXTENSIONS}
          deletingIds={deletingIds}
          onLoadMore={handleLoadMoreData}
          onBeforeUpload={handleBeforeUpload}
          onDelete={handleDelete}
          onRename={handleRename}
          // onCancelUpload={handleCancelUpload}
          onAddToAlbum={handleAddAttachments}
          showAddtoAlbum={addFilesAllowed}
        />
      )}
    </Flex>
  )
}

const mapStateToProps = (state: RootState) => ({
  userId: state.auth.userInfo.id,
  fetching: state.album.fetchingAttachments,
  data: state.album.attachments,
  filters: state.album.attachmentFilters,
  projectPermissions: selectProjectWritePermissions(state)
})

const mapDispatchToProps = {
  getFilterOptions: getAttachmentsFilterOptions,
  setProp: albumSetProp,
  getAttachments,
  getMoreAttachments,
  uploadAttachment: uploadProjectAttachment,
  deleteAttachment: deleteProjectAttachment,
  renameAttachment,
  addAlbumAttachments
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ProjectViewAllFilesSection)
